import {
  Avatar,
  Box,
  Button,
  ButtonProps,
  Container,
  Divider,
  Grid,
  ListItem,
  ListItemAvatar,
  ListItemText,
  makeStyles,
  MobileStepper,
  Typography
} from '@material-ui/core';
import { ChevronRight, KeyboardArrowLeft, KeyboardArrowRight } from '@material-ui/icons';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import { PageProps } from 'gatsby';
import { StaticImage } from 'gatsby-plugin-image';
import React from 'react';
import SwipeableViews from 'react-swipeable-views';
import { autoPlay } from 'react-swipeable-views-utils';
import CodeBlock from '../components/code-block';
import {
  FeatureTab,
  FeatureTabContent,
  FeatureTabDetails,
  FeatureTabSummary
} from '../components/feature-tabs';
import Link from '../components/link';
import PageLayout from '../components/page-layout';
import Section, { SectionTitle } from '../components/section';
import Seo from '../components/seo';
import { StyledCard, StyledCardSeparator } from '../components/styled-card';

const AutoPlaySwipeableViews = autoPlay(SwipeableViews);

const useStyles = makeStyles(theme => ({
  heroSubtitle: {
    marginTop: theme.spacing(2),
  },
  heroBtn: {
    marginTop: theme.spacing(4),
  },
  title: {
    [theme.breakpoints.up('md')]: {
      paddingRight: theme.spacing(8),
    },
    [theme.breakpoints.down('sm')]: {
      paddingBottom: theme.spacing(8),
    },
  },
  iconImgWrapper: {
    textAlign: 'center',
    marginBottom: theme.spacing(6),
  },
  iconHeader: {
    marginBottom: theme.spacing(4),
    letterSpacing: '0.1em',
    fontWeight: '700 !important' as any,
    textTransform: 'uppercase',
  },
  gutterBottom: {
    marginBottom: theme.spacing(2),
  },
}));

const LearnMoreButton = (props: ButtonProps) => (
  <Button
    {...props}
    color="primary"
    endIcon={<ChevronRightIcon />}
    disableRipple
    style={{ backgroundColor: 'transparent', padding: 0, marginTop: 10, textTransform: 'none' }}
  >
    Learn more
  </Button>
);

const HomePage: React.FC<PageProps> = props => {
  const classes = useStyles();
  const [selectedDeveloperTab, setDeveloperTab] = React.useState<string>('declarative-config');

  const customers = [
    {
      companyImage: '/img/customers/decisionpointhealth.png',
      companyName: 'Decision Point',
    },
    {
      companyImage: '/img/customers/simplecommands.svg',
      companyName: 'SimpleCommands',
    },
    {
      companyImage: '/img/customers/zylotech.png',
      companyName: 'Zylotech',
    },
  ];

  const testimonials = [
    {
      companyImage: '/img/customers/decisionpointhealth.png',
      companyName: 'Decision Point',
      userImage: '/img/customers/dphs-billy.jpg',
      userName: 'Stanis Laus Billy',
      title: 'Vice President of Engineering',
      text: 'Our team struggled with synchronizing our environments and wasted the time of our most senior engineers. Architect simplified deployments so much that it not only improved synchronization but also reduced our engineering overhead. What used to take 3 senior engineers to do is now no more than a part-time job.',
    },
    {
      companyImage: '/img/customers/sproutward.svg',
      companyName: 'Sproutward',
      userImage: '/img/customers/sproutward-nathan.jpg',
      userName: 'Nathan Wade',
      title: 'Senior Director of Engineering',
      text: 'Our company has strict protocols to ensure customer data is protected, but maintaining parallel customer environments has been a nightmare. Architect’s automated environment provisioning helped us re-focus on feature development and less on infra - as it should be!',
    },
  ];

  const [activeStep, setActiveStep] = React.useState(0);
  const maxSteps = testimonials.length;

  const handleNext = () => {
    setActiveStep(prevActiveStep => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep(prevActiveStep => prevActiveStep - 1);
  };

  const handleStepChange = step => {
    setActiveStep(step);
  };

  return (
    <PageLayout>
      <Seo title="DevOps, Supercharged" />

      <Section variant="hero-home" verticalSpacing={8}>
        <Grid container justifyContent="space-evenly" alignItems="center">
          <Grid item md={7} className={classes.title}>
            <Typography variant="h2" component="h1" paragraph>
              Previews, production, and everything in between
            </Typography>
            <Typography variant="subtitle1" paragraph className={classes.heroSubtitle}>
              Democratize deployments and environment creation with our dependency-aware continuous
              delivery platform
            </Typography>
            <Button
              variant="contained"
              color="primary"
              size="large"
              className={classes.heroBtn}
              href="//cloud.architect.io/signup"
            >
              Get started for free
            </Button>
          </Grid>
          <Grid item md={5}>
            <StaticImage
              src="../images/hero-computer-screen.svg"
              alt="Previews, production, and everything in between."
              loading="eager"
            />
          </Grid>
        </Grid>
      </Section>

      <Section>
        <SectionTitle
          title="Let us automate that"
          subtitle="DevOps is the intersection of several tools and processes. All of which can be automated with Architect."
        />

        <Grid container justifyContent="space-evenly" spacing={4} style={{ textAlign: 'center' }}>
          <Grid item md={4}>
            <div className={classes.iconImgWrapper}>
              <StaticImage
                src="../images/icons/sm/dependency-management.svg"
                alt="Application delivery"
                height={120}
              />
            </div>
            <Typography variant="h6" paragraph className={classes.iconHeader}>
              Application delivery
            </Typography>
            <Typography variant="body2" paragraph>
              Developers waste hours of time writing tedious pipelining scripts and babysitting
              deployments. Help your developers focus on your product, and let us automate that.
            </Typography>
            <Link to="/product/application-delivery">
              <Button color="primary" variant="text" endIcon={<ChevronRight />}>
                Learn more
              </Button>
            </Link>
          </Grid>
          <Grid item md={4}>
            <div className={classes.iconImgWrapper}>
              <StaticImage
                src="../images/icons/sm/securing-networks.svg"
                alt="Securing your networks"
                height={120}
              />
            </div>
            <Typography variant="h6" paragraph className={classes.iconHeader}>
              Network security
            </Typography>
            <Typography variant="body2" paragraph>
              Network security is critical to protecting your customers and business, but
              instrumentation is tedious and error prone. Let us automate that.
            </Typography>
            <Link to="/product/network-security">
              <Button color="primary" variant="text" endIcon={<ChevronRight />}>
                Learn more
              </Button>
            </Link>
          </Grid>
          <Grid item md={4}>
            <div className={classes.iconImgWrapper}>
              <StaticImage
                src="../images/icons/sm/provisioning.svg"
                alt="Provisioning environments"
                height={120}
              />
            </div>
            <Typography variant="h6" paragraph className={classes.iconHeader}>
              Environment provisioning
            </Typography>
            <Typography variant="body2" paragraph>
              Developers need to run and test their applications in private sandboxes and preview
              environments, but DevOps engineers never have time to support temporary environments.
              Let us automate that.
            </Typography>
            <Link to="/product/environment-provisioning">
              <Button color="primary" variant="text" endIcon={<ChevronRight />}>
                Learn more
              </Button>
            </Link>
          </Grid>
        </Grid>
      </Section>

      <Container>
        <Divider />
      </Container>

      <Section>
        <SectionTitle
          title="DevOps starts with Developers"
          subtitle="Our cloud platform and Architecture-as-Code framework are everything developers need to build and ship new apps and services to production-grade environments."
        />

        <Grid container justifyContent="space-around" spacing={4}>
          <Grid item md={6} xs={12}>
            <FeatureTab
              expanded={selectedDeveloperTab === 'declarative-config'}
              onChange={() => setDeveloperTab('declarative-config')}
            >
              <FeatureTabSummary>
                <Typography variant="subtitle1">Declarative config files</Typography>
              </FeatureTabSummary>
              <FeatureTabDetails>
                <Typography variant="body2">
                  Define your apps, services, and architecture using a declarative configuration
                  file so that your stack can be run anywhere, by anyone, for any reason
                </Typography>
                <Link to="/docs/components/architect-yml">
                  <LearnMoreButton>Learn more</LearnMoreButton>
                </Link>
              </FeatureTabDetails>
            </FeatureTab>

            <FeatureTab
              expanded={selectedDeveloperTab === 'discovery-security'}
              onChange={() => setDeveloperTab('discovery-security')}
            >
              <FeatureTabSummary>
                <Typography variant="subtitle1">Intuitive service discovery</Typography>
              </FeatureTabSummary>
              <FeatureTabDetails>
                <Typography variant="body2">
                  Different environments use different hostnames and tools for service discovery,
                  load balancing, and security. Don't let that slow down development and let
                  Architect configure it for you.
                </Typography>
                <Link to="/product/service-discovery">
                  <LearnMoreButton>Learn more</LearnMoreButton>
                </Link>
              </FeatureTabDetails>
            </FeatureTab>

            <FeatureTab
              expanded={selectedDeveloperTab === 'dependency-management'}
              onChange={() => setDeveloperTab('dependency-management')}
            >
              <FeatureTabSummary>
                <Typography variant="subtitle1">External dependency injection</Typography>
              </FeatureTabSummary>
              <FeatureTabDetails>
                <Typography variant="body2">
                  Your app is distributed, and so is the knowledge of its architecture. Leverage
                  familiar dependency management practices to connect to databases, message queues,
                  peer APIs, and other external dependencies.
                </Typography>
                <Link to="/docs/components/dependencies">
                  <LearnMoreButton>Learn more</LearnMoreButton>
                </Link>
              </FeatureTabDetails>
            </FeatureTab>

            <FeatureTab
              expanded={selectedDeveloperTab === 'dev-environments'}
              onChange={() => setDeveloperTab('dev-environments')}
            >
              <FeatureTabSummary>
                <Typography variant="subtitle1">Private developer environments</Typography>
              </FeatureTabSummary>
              <FeatureTabDetails>
                <Typography variant="body2">
                  Running and testing features and services is critical for developer productivity.
                  Architect's automation isn't just for production environments, but for private
                  developer environments as well.
                </Typography>
                <Link to="/docs/deployments/local-environments">
                  <LearnMoreButton>Learn more</LearnMoreButton>
                </Link>
              </FeatureTabDetails>
            </FeatureTab>

            <FeatureTab
              expanded={selectedDeveloperTab === 'preview-environments'}
              onChange={() => setDeveloperTab('preview-environments')}
            >
              <FeatureTabSummary>
                <Typography variant="subtitle1">Preview environments</Typography>
              </FeatureTabSummary>
              <FeatureTabDetails>
                <Typography variant="body2">
                  The earlier a feature can be tested in a production-like environment, the faster
                  the changes can be trusted and released. Architect's automation enables full
                  environments to be provisioned and destroyed with each pull request.
                </Typography>
                <Link to="/docs/deployments/automated-previews">
                  <LearnMoreButton>Learn more</LearnMoreButton>
                </Link>
              </FeatureTabDetails>
            </FeatureTab>

            <FeatureTab
              expanded={selectedDeveloperTab === 'production-environments'}
              onChange={() => setDeveloperTab('production-environments')}
            >
              <FeatureTabSummary>
                <Typography variant="subtitle1">Self-service deployments</Typography>
              </FeatureTabSummary>
              <FeatureTabDetails>
                <Typography variant="body2">
                  Consistency is the key to successful deployments. Deploying with Architect is the
                  same for every service and every environment, making production deployments safe
                  and reliable for every developer on your team.
                </Typography>
                <Link to="/docs/deployments/gitops-releases">
                  <LearnMoreButton>Learn more</LearnMoreButton>
                </Link>
              </FeatureTabDetails>
            </FeatureTab>
          </Grid>
          <Grid item md={6} xs={12}>
            <FeatureTabContent visible={selectedDeveloperTab === 'declarative-config'}>
              <CodeBlock name="declarative-config" />
            </FeatureTabContent>

            <FeatureTabContent visible={selectedDeveloperTab === 'discovery-security'}>
              <CodeBlock name="discovery-and-security" />
            </FeatureTabContent>

            <FeatureTabContent visible={selectedDeveloperTab === 'dependency-management'}>
              <CodeBlock name="dependency-management" />
            </FeatureTabContent>

            <FeatureTabContent visible={selectedDeveloperTab === 'dev-environments'}>
              <CodeBlock name="private-dev-environments" />
            </FeatureTabContent>

            <FeatureTabContent visible={selectedDeveloperTab === 'preview-environments'}>
              <CodeBlock name="gitops-previews" />
            </FeatureTabContent>

            <FeatureTabContent visible={selectedDeveloperTab === 'production-environments'}>
              <img
                src="/img/styled-screenshots/marquee@2x.png"
                alt="hero"
                style={{ maxWidth: '100%' }}
              />
            </FeatureTabContent>
          </Grid>
        </Grid>
      </Section>

      <Container>
        <Divider />
      </Container>

      <Section>
        <SectionTitle
          title="Distributed apps, centralized governance"
          subtitle="Our cloud platform gives you visibility, protection, and compliance without sacrificing productivity"
        />

        <Grid container justifyContent="center" spacing={4}>
          <Grid item md={4} sm={6}>
            <StyledCard>
              <StaticImage
                src="../images/icons/sm/service-catalog.png"
                alt="Service catalog"
                height={120}
                className={classes.gutterBottom}
              />

              <Typography variant="h5" gutterBottom>
                Application &amp;
                <br />
                service catalog
              </Typography>

              <StyledCardSeparator />

              <Typography variant="body2">
                View your full portfolio of apps and services, and share it with your team to ensure
                that no one is re-inventing the wheel.
              </Typography>
            </StyledCard>
          </Grid>

          <Grid item md={4} sm={6}>
            <StyledCard>
              <StaticImage
                src="../images/icons/sm/cluster-management.png"
                alt="Cluster management"
                height={120}
                className={classes.gutterBottom}
              />

              <Typography variant="h5" gutterBottom>
                Cluster management
                <br />
                &amp; access control
              </Typography>

              <StyledCardSeparator />

              <Typography variant="body2">
                Architect helps teams create, manage, and distribute access to the clusters that
                will play host to production and developer environments alike.
              </Typography>
            </StyledCard>
          </Grid>

          <Grid item md={4} sm={6}>
            <StyledCard>
              <StaticImage
                src="../images/icons/sm/centralized-governance.svg"
                alt="Centralized governance"
                height={120}
                className={classes.gutterBottom}
              />

              <Typography variant="h5" gutterBottom>
                Centralized
                <br />
                governance
              </Typography>

              <StyledCardSeparator />

              <Typography variant="body2">
                Visualize and assign policies for all of your production and non-production
                environments with Architect Cloud. Deployments will automatically inject policies
                and settings without impacting developer productivity.
              </Typography>
            </StyledCard>
          </Grid>

          <Grid item md={4} sm={6}>
            <StyledCard>
              <StaticImage
                src="../images/icons/sm/zero-trust.svg"
                alt="Zero trust"
                height={120}
                className={classes.gutterBottom}
              />

              <Typography variant="h5" gutterBottom>
                Zero trust
                <br />
                cloud security
              </Typography>

              <StyledCardSeparator />

              <Typography variant="body2">
                Architect's deploy process includes the unique ability to enrich deployments with
                strict network policies, ensuring that environments are as secure as possible at all
                times.
              </Typography>
            </StyledCard>
          </Grid>

          <Grid item md={4} sm={6}>
            <StyledCard>
              <StaticImage
                src="../images/icons/sm/audit-trail.png"
                alt="Audit trail"
                height={120}
                className={classes.gutterBottom}
              />

              <Typography variant="h5" gutterBottom>
                Compliance &amp;
                <br />
                audit trails
              </Typography>

              <StyledCardSeparator />

              <Typography variant="body2">
                Every deployment comes complete with a detailed breakdown of the changes applied,
                how long the deployment took, and who triggered the deployment – all the information
                needed to maintain compliance and protect your business.
              </Typography>
            </StyledCard>
          </Grid>
        </Grid>
      </Section>

      <Container>
        <Divider />
      </Container>

      <Section>
        <SectionTitle
          title="Tried, Tested, Trusted."
          subtitle="Architect is used by the best in the business from development to production"
        />

        <div style={{ marginBottom: 60 }}>
          <Grid container justifyContent="center" spacing={3}>
            {customers.map((customer, index) => {
              return (
                <Grid item xs={12} md={3} key={customer.companyName} align="center">
                  <img src={customer.companyImage} alt={customer.companyName} height="30px" />
                </Grid>
              );
            })}
          </Grid>
        </div>

        <Grid container justifyContent="center">
          <Grid item sm={9} md={6}>
            <StyledCard minHeight="none">
              <AutoPlaySwipeableViews
                axis="x"
                index={activeStep}
                onChangeIndex={handleStepChange}
                enableMouseEvents
                interval={10000}
              >
                {testimonials.map(testimonial => {
                  return (
                    <Box textAlign="left" key={testimonial.companyName}>
                      <img
                        src={testimonial.companyImage}
                        alt={testimonial.companyName}
                        width="150px"
                        style={{ marginBottom: 10 }}
                      />

                      <Typography>{testimonial.text}</Typography>

                      <StyledCardSeparator marginLeft="none" width="20%" />

                      <ListItem>
                        <ListItemAvatar>
                          <Avatar alt={testimonial.userName} src={testimonial.userImage} />
                        </ListItemAvatar>
                        <ListItemText
                          primary={testimonial.userName}
                          secondary={testimonial.title}
                        />
                      </ListItem>
                    </Box>
                  );
                })}
              </AutoPlaySwipeableViews>
            </StyledCard>
            <Box m={2}>
              <MobileStepper
                variant="progress"
                steps={maxSteps}
                position="static"
                activeStep={activeStep}
                nextButton={
                  <Button size="small" onClick={handleNext} disabled={activeStep === maxSteps - 1}>
                    <KeyboardArrowRight />
                  </Button>
                }
                backButton={
                  <Button size="small" onClick={handleBack} disabled={activeStep === 0}>
                    <KeyboardArrowLeft />
                  </Button>
                }
              />
            </Box>
          </Grid>
        </Grid>
      </Section>
    </PageLayout>
  );
};

export default HomePage;
